import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Typography } from '@/components/ui/typography';
import { useNavigationService } from '../../services/navigate-service';
import { ROUTES } from '../../config/routes';
import data from './mock-data/faq.json';
import FaqFooter from '../../asset/images/faq-footer.svg';

const faqImages = import.meta.glob('../../asset/images/faq/*', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

const SEARCH_MAX_LENGTH = 350;

type FaqSection = (typeof data.sections)[number];

const getFaqImage = (filename: string): string | undefined => {
  const key = Object.keys(faqImages).find((k) => k.endsWith(`/${filename}`));
  return key ? faqImages[key] : undefined;
};

const stripHtml = (text: string): string =>
  text.replace(/<\/?[^>]+(>|$)/g, '');

// User input goes into RegExp; escape so characters like `(` or `.` are treated literally.
const escapeRegex = (text: string): string =>
  text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const filterFaqData = (
  sections: FaqSection[],
  searchTerm: string,
): { filteredSections: FaqSection[]; resultCount: number } => {
  if (!searchTerm) return { filteredSections: sections, resultCount: 0 };

  const pattern = new RegExp(escapeRegex(searchTerm), 'gi');
  const countMatches = (text: string): number =>
    (stripHtml(text).match(pattern) ?? []).length;

  let resultCount = 0;
  const filteredSections = sections
    .map((section) => {
      const items = section.items.filter((item) => {
        const matches =
          countMatches(item.question) +
          item.answers.reduce(
            (sum, ans) => sum + countMatches(ans.answer),
            0,
          );
        resultCount += matches;
        return matches > 0;
      });
      return { ...section, items };
    })
    .filter((section) => section.items.length > 0);

  return { filteredSections, resultCount };
};

interface FaqSectionViewProps {
  section: FaqSection;
  sectionIdx: number;
  openItems: string[];
  onOpenChange: (keys: string[]) => void;
  highlightHtml: (text: string) => string;
}

const FaqSectionView = ({
  section,
  sectionIdx,
  openItems,
  onOpenChange,
  highlightHtml,
}: FaqSectionViewProps) => (
  <div className="pt-4">
    <Typography as="h3" className="text-xl font-semibold">
      {section.title}
    </Typography>
    <Accordion type="multiple" value={openItems} onValueChange={onOpenChange}>
      {section.items.map((item, itemIdx) => {
        const key = `${sectionIdx}-${itemIdx}`;
        const questionAlt = stripHtml(item.question);
        return (
          <AccordionItem value={key} key={key}>
            <AccordionTrigger>
              <span
                dangerouslySetInnerHTML={{
                  __html: highlightHtml(item.question),
                }}
              />
            </AccordionTrigger>
            <AccordionContent>
              {item.answers.map((answerObj, ansIdx) => (
                <div key={ansIdx}>
                  <div
                    className="py-3"
                    dangerouslySetInnerHTML={{
                      __html: highlightHtml(answerObj.answer),
                    }}
                  />
                  {answerObj.imageUrl && (
                    <img
                      src={getFaqImage(answerObj.imageUrl)}
                      alt={questionAlt}
                      className="mb-4 h-auto max-w-full rounded-lg"
                    />
                  )}
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  </div>
);

const FaqPage = () => {
  const { navigateTo } = useNavigationService();
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    document.title = 'PlanOmatic - FAQ';
    return () => {
      document.title = 'PlanOmatic';
    };
  }, []);

  const { filteredSections, resultCount } = useMemo(
    () => filterFaqData(data.sections, searchTerm),
    [searchTerm],
  );

  useEffect(() => {
    if (!searchTerm) {
      setOpenItems([]);
      return;
    }
    const allKeys = filteredSections.flatMap((section, sectionIdx) =>
      section.items.map((_, itemIdx) => `${sectionIdx}-${itemIdx}`),
    );
    setOpenItems(allKeys);
  }, [filteredSections, searchTerm]);

  const highlightHtml = (text: string): string => {
    if (!searchTerm) return text;
    const pattern = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    return text.replace(pattern, '<mark class="bg-yellow-200">$1</mark>');
  };

  const onNavigateToContact = () => navigateTo(ROUTES.HELP_CONTACT);

  return (
    <section aria-labelledby="faq-page-heading">
      <div className="border-b border-border px-5 py-4">
        <Typography as="h1" variant="heading-xl" id="faq-page-heading">
          Frequently Asked Questions
        </Typography>
        <div className="relative mt-3 max-w-[650px]">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id="search-faq"
            type="search"
            aria-label="Search Help Articles"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search Help Articles..."
            maxLength={SEARCH_MAX_LENGTH}
            data-testid="search-faq_id"
            className="pl-10"
          />
        </div>
      </div>

      <div className="px-5 pt-6">
        <Typography as="h2" variant="heading-lg" className="font-semibold">
          {searchTerm
            ? `${resultCount} Search results found for "${searchTerm}"`
            : 'Browse by Topics'}
        </Typography>
      </div>

      <div className="px-5">
        {filteredSections.map((section, sectionIdx) => (
          <FaqSectionView
            key={sectionIdx}
            section={section}
            sectionIdx={sectionIdx}
            openItems={openItems}
            onOpenChange={setOpenItems}
            highlightHtml={highlightHtml}
          />
        ))}
      </div>

      <div className="px-5 pt-5 pb-6">
        <div className="grid grid-cols-1 items-center gap-4 rounded-lg border border-border p-6 md:grid-cols-[2fr_1fr]">
          <div>
            <Typography as="h2" className="mb-3 text-lg font-semibold">
              Didn&apos;t find answer to your questions?
            </Typography>
            <Typography as="p" variant="body" className="text-muted-foreground">
              Our team is here to help you and provide expert advice, please
              contact us with your queries.
            </Typography>
            <div className="mt-4">
              <Button onClick={onNavigateToContact}>Contact Us</Button>
            </div>
          </div>
          <div className="text-center">
            {/* Decorative promo graphic: 200px tall is desktop-tuned and eats
                a third of a 375px viewport, so it scales down below the md
                breakpoint, where the CTA block is still stacked. */}
            <img
              src={FaqFooter}
              alt="Help Center"
              className="h-[120px] w-full object-contain md:h-[200px]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaqPage;
